import React, { useState } from "react";

// Icon imports
import CloseCircleIcon from "assets/breakfast-event/svgs/close-circle.svg";
import LampChargeIcon from "assets/breakfast-event/svgs/lamp-charge.svg";
import axios from "axios";
import { toast } from "react-toastify";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { object, string } from "yup";
import { useTranslation } from "react-i18next";

const PartnerWaitlistModal = ({ isOpen, onClose }) => {
  const { t } = useTranslation("event");

  const [submitting, setSubmitting] = useState(false);

  const validationSchema = object().shape({
    company_name: string().required("Please enter company name"),
    work_email: string()
      .required("Please provide a valid business email")
      .matches(
        /^[a-zA-Z0-9._%+-]+@(?!gmail\.com$)(?!yahoo\.com$)(?!hotmail\.com$)(?!outlook\.com$)[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
        "Please use a business email, not a personal email"
      ),
    phone_number: string()
      .required("Please enter your phone number")
      .matches(/^\d+$/, "Phone number must contain only digits")
      .min(7, "Phone number must be at least 7 digits")
      .max(15, "Phone number must not exceed 15 digits"),
  });

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm({ resolver: yupResolver(validationSchema) });

  const closeModal = () => {
    onClose(false);
    reset();
    setSubmitting(false);
  };

  const onSubmit = async (values) => {
    setSubmitting(true);
    const response = await axios.post(
      "https://api.smartcomplyapp.com/api/user_mgt/breakfast_event_registration/",
      values
    );

    setSubmitting(false);

    if (response?.data?.status === "success") {
      toast.success(`Request submitted successfully`);
      reset();
      setSubmitting(false);
      closeModal();
    } else {
      toast.error(response?.message);
    }
  };
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/60" onClick={onClose} />

      {/* Modal content */}
      <div className="relative bg-white rounded-2xl w-[90%] max-w-lg z-10">
        <div className="flex justify-between items-center border-b border-[#E2E8F0] px-6 py-3">
          <h2 className="text-lg font-semibold text-[#334155]">
            {t("partnersModal.title")}
          </h2>

          <button className="bg-none border-none" onClick={onClose}>
            <img src={CloseCircleIcon} alt="CloseCircleIcon" />
          </button>
        </div>

        <form className="" onSubmit={handleSubmit(onSubmit)}>
          <div className="space-y-4 p-6">
            <div className="bg-[#F1F5F9] border border-[#F1F5F9] flex items-start gap-1 rounded-[12px] px-4 py-2">
              <img src={LampChargeIcon} alt="LampChargeIcon" />
              <p className="text-[#475569] text-[13px] font-normal">
                {t("partnersModal.infoText")}
              </p>
            </div>
            <div>
              <label className="block text-[#475569] text-sm font-normal !mb-1">
                {t("partnersModal.companyName")}
                <span className="text-[#F43F5E]">*</span>
              </label>
              <input
                type="text"
                className="w-full border rounded-lg px-3 py-2 putline-none focus:outline-none"
                placeholder={t("partnersModal.companyNamePlaceholder")}
                {...register("company_name")}
              />
              {errors.company_name && (
                <p className="text-[red] text-[13px] mt-1">
                  {errors.company_name.message}
                </p>
              )}
            </div>

            <div>
              <label className="block text-[#475569] text-sm font-normal !mb-1">
                {t("partnersModal.workEmail")}{" "}
                <span className="text-[#F43F5E]">*</span>
              </label>
              <input
                type="email"
                className="w-full border rounded-lg px-3 py-2 focus:outline-none"
                placeholder={t("partnersModal.workEmailPlaceholder")}
                {...register("work_email")}
              />
              {errors.work_email && (
                <p className="text-[red] text-[13px] mt-1">
                  {errors.work_email.message}
                </p>
              )}
            </div>

            <div>
              <label className="block text-[#475569] text-sm font-normal !mb-1">
                {t("partnersModal.phone")}{" "}
                <span className="text-[#F43F5E]">*</span>
              </label>
              <input
                type="text"
                className="w-full border rounded-lg px-3 py-2 focus:outline-none"
                placeholder={t("partnersModal.phonePlaceholder")}
                {...register("phone_number")}
              />

              {errors.phone_number && (
                <p className="text-[red] text-[13px] mt-1">
                  {errors.phone_number.message}
                </p>
              )}
            </div>
          </div>
          <div className="border-t border-[#E2E8F0] flex justify-end items-center gap-2 py-4 px-6">
            <button
              onClick={onClose}
              type="button"
              className="w-fit bg-none border border-[#E2E8F0] text-[#475569] text-sm px-4 py-0.5 rounded-lg hover:opacity-90 font-semibold"
            >
              {t("partnersModal.cancelCta")}
            </button>
            <button
              type="submit"
              className="w-fit bg-[#202D66] text-white text-sm px-4 py-1 rounded-lg hover:opacity-90 font-semibold flex items-center gap-1"
            >
              {t("partnersModal.submitPlaceholder")}
              {submitting && <div className="loader w-[10px] h-[10px]"></div>}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default PartnerWaitlistModal;
